import { useState, useCallback, useRef, useEffect } from 'react';
import type { FrecuenciaSettings } from '@/mocks/adminSettingsRadio';
import { formatosStream, bitrates } from '@/mocks/adminSettingsRadio';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  simulateVerification,
  alertMessages,
  knownStreamUrls,
  type StreamVerificationResult,
} from '@/mocks/streamVerification';
import { stationsBackend } from '@/services/stations';
import Swal from 'sweetalert2';
import type { StationControlResult, StationSyncStatus } from '@/types/stations';
import ManagedStreamingSection from './ManagedStreamingSection';
import type { ManagedStreamingDetails } from '@/types/managedStreaming';

type VerifyPhase = 'idle' | 'connecting' | 'checking' | 'done';
type ControlAction = 'START' | 'STOP' | 'RESTART' | 'RELOAD';

const phaseLabels: Record<VerifyPhase, string> = {
  idle: '',
  connecting: 'Conectando al servidor...',
  checking: 'Analizando stream de audio...',
  done: '',
};

/** Operaciones que requieren confirmación antes de ejecutarse */
const CONFIRM_REQUIRED: ControlAction[] = ['STOP', 'RESTART', 'RELOAD'];

const confirmMessages: Record<ControlAction, { title: string; text: string; confirmLabel: string }> = {
  STOP: {
    title: '¿Detener transmisión?',
    text: 'Esto va a interrumpir la señal. Los oyentes dejarán de recibir audio.',
    confirmLabel: 'Sí, detener',
  },
  RESTART: {
    title: '¿Reiniciar transmisión?',
    text: 'La transmisión se va a interrumpir brevemente mientras se reinicia el servidor.',
    confirmLabel: 'Sí, reiniciar',
  },
  RELOAD: {
    title: '¿Recargar configuración?',
    text: 'Se va a recargar la configuración del servidor sin interrumpir la transmisión.',
    confirmLabel: 'Sí, recargar',
  },
  START: {
    title: '',
    text: '',
    confirmLabel: '',
  },
};

const actionLabels: Record<ControlAction, string> = {
  START: 'Iniciar',
  STOP: 'Detener',
  RESTART: 'Reiniciar',
  RELOAD: 'Recargar',
};

interface Props {
  frecuencia: FrecuenciaSettings;
  onChangeFrecuencia: (data: FrecuenciaSettings) => void;
  onSave: () => void;
  saving: boolean;
  /** ID de la estación en Supabase. Cuando está presente los controles usan el backend PHP real. */
  stationId?: string;
  /** Datos del streaming administrado por RFW */
  managedStreaming?: ManagedStreamingDetails;
  /** Callback para refrescar datos de managed streaming */
  onManagedStreamingRefresh?: () => void;
}

export default function SettingsStreamingTab({ frecuencia, onChangeFrecuencia, onSave, saving, stationId, managedStreaming, onManagedStreamingRefresh }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [testUrl, setTestUrl] = useState(frecuencia.streamUrl || knownStreamUrls[0].url);
  const [phase, setPhase] = useState<VerifyPhase>('idle');
  const [result, setResult] = useState<StreamVerificationResult | null>(null);
  const [alert, setAlert] = useState<typeof alertMessages[0] | null>(null);
  const [history, setHistory] = useState<StreamVerificationResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [controlLoading, setControlLoading] = useState<ControlAction | null>(null);
  const [controlResult, setControlResult] = useState<StationControlResult | null>(null);
  const [controlError, setControlError] = useState<string | null>(null);
  const pendingRef = useRef(false);

  // ── Sync state ──
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState<StationSyncStatus | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncAttemptsRef = useRef(0);
  const MAX_SYNC_ATTEMPTS = 24; // 2 minutos a 5 segundos por intento

  const verifyInProgress = phase !== 'idle' && phase !== 'done';

  const validate = useCallback((): boolean => {
    const url = frecuencia.streamUrl.trim();
    if (!url) {
      SwalAlert.warning('Stream URL requerida', 'Debés ingresar la URL de tu servidor de streaming.');
      return false;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      SwalAlert.warning('Stream URL inválida', 'La URL debe comenzar con http:// o https://');
      return false;
    }
    if (!frecuencia.frecuenciaNum.trim()) {
      SwalAlert.warning('Frecuencia requerida', 'Ingresá la frecuencia de emisión (ej: 102.5 FM).');
      return false;
    }
    return true;
  }, [frecuencia]);

  const handleSave = () => {
    if (validate()) onSave();
  };

  const handleVerify = async () => {
    if (verifyInProgress) return;
    const trimmed = testUrl.trim();
    if (!trimmed) return;
    setPhase('connecting');
    setResult(null);
    setAlert(null);
    setTimeout(() => setPhase('checking'), 1200);
    try {
      const res = await simulateVerification(trimmed);
      setPhase('done');
      setResult(res);
      const matchedAlert = alertMessages.find((a) => a.type === res.alert) || null;
      setAlert(matchedAlert);
      setHistory((prev) => [res, ...prev].slice(0, 10));
    } catch { setPhase('idle'); }
  };

  // ── Control de estación ──

  const executeControlAction = useCallback(async (action: ControlAction) => {
    if (pendingRef.current || controlLoading || !stationId) return;

    // Confirmación para STOP, RESTART, RELOAD
    if (CONFIRM_REQUIRED.includes(action)) {
      const msg = confirmMessages[action];
      const confirmed = await Swal.fire({
        title: msg.title,
        text: msg.text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: msg.confirmLabel,
        cancelButtonText: 'Cancelar',
        confirmButtonColor: action === 'STOP' ? '#ef4444' : '#f59e0b',
        cancelButtonColor: '#d1d5db',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold text-sm',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
          cancelButton: 'rounded-lg px-5 py-2 text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-600',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
      if (!confirmed.isConfirmed) return;
    }

    pendingRef.current = true;
    setControlLoading(action);
    setControlError(null);
    setControlResult(null);

    try {
      let res: StationControlResult;
      switch (action) {
        case 'START':
          res = await stationsBackend.startStation(stationId);
          break;
        case 'STOP':
          res = await stationsBackend.stopStation(stationId);
          break;
        case 'RESTART':
          res = await stationsBackend.restartStation(stationId);
          break;
        case 'RELOAD':
          res = await stationsBackend.reloadStation(stationId);
          break;
      }
      setControlResult(res);

      // Sincronizar estado local con lo que devuelve el backend
      const newEstado: 'online' | 'offline' =
        res.status === 'online' || res.status === 'starting' ? 'online' : 'offline';
      onChangeFrecuencia({ ...frecuencia, estado: newEstado });

      SwalAlert.toast(
        `${actionLabels[action]}: ${res.message || 'Operación completada.'}`,
        'success',
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al ejecutar la operación.';
      setControlError(msg);
      SwalAlert.toast(msg, 'error');
    } finally {
      pendingRef.current = false;
      setControlLoading(null);
    }
  }, [controlLoading, stationId, frecuencia, onChangeFrecuencia]);

  // Fallback local cuando no hay stationId (comportamiento actual del mock)
  const handleToggleLocal = useCallback(() => {
    onChangeFrecuencia({
      ...frecuencia,
      estado: frecuencia.estado === 'online' ? 'offline' : 'online',
    });
  }, [frecuencia, onChangeFrecuencia]);

  const handleControlClick = useCallback((action: ControlAction) => {
    if (stationId) {
      executeControlAction(action);
    } else {
      // Sin stationId, START y STOP actúan como toggle local
      if (action === 'START') {
        if (frecuencia.estado === 'offline') handleToggleLocal();
      } else if (action === 'STOP') {
        if (frecuencia.estado === 'online') handleToggleLocal();
      }
    }
  }, [stationId, executeControlAction, handleToggleLocal, frecuencia.estado]);

  // ── Sync ──

  /**
   * Detiene el polling de sync de forma segura.
   */
  const stopSyncPolling = useCallback(() => {
    if (syncPollRef.current !== null) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
  }, []);

  /**
   * Consulta el estado de la sincronización cada 5 segundos.
   * Se detiene al completar, fallar, entrar en conflicto,
   * desmontar el componente, o alcanzar el máximo de intentos.
   *
   * Evita solicitudes simultáneas con un flag interno.
   */
  const pollSyncStatus = useCallback(async (stationIdParam: string) => {
    stopSyncPolling();

    syncAttemptsRef.current = 0;

    syncPollRef.current = setInterval(async () => {
      syncAttemptsRef.current += 1;

      if (syncAttemptsRef.current > MAX_SYNC_ATTEMPTS) {
        stopSyncPolling();
        setSyncLoading(false);
        setSyncStatus('FAILED');
        setSyncError('La sincronización está tardando más de lo esperado. Revisá manualmente el estado de la estación.');
        return;
      }

      try {
        const res = await stationsBackend.getStationSyncStatus(stationIdParam);

        // Solo procesar si el polling sigue activo
        if (syncPollRef.current === null) return;

        setSyncStatus(res.status);
        setSyncMessage(res.message);

        if (res.status === 'COMPLETED') {
          stopSyncPolling();
          setSyncLoading(false);
          setSyncError(null);
          setLastSyncAt(res.timestamp);
          SwalAlert.toast('Sincronización completada.', 'success');
        } else if (res.status === 'FAILED') {
          stopSyncPolling();
          setSyncLoading(false);
          setSyncError(res.message || 'La sincronización falló.');
          SwalAlert.toast(res.message || 'La sincronización falló.', 'error');
        } else if (res.status === 'CONFLICT') {
          stopSyncPolling();
          setSyncLoading(false);
          setSyncError(null);
          setSyncMessage(res.message);
          SwalAlert.toast('Se detectó un conflicto. Requiere revisión administrativa.', 'warning');
        }
      } catch (err: unknown) {
        if (syncPollRef.current === null) return;
        stopSyncPolling();
        setSyncLoading(false);
        setSyncStatus('FAILED');
        const msg = err instanceof Error ? err.message : 'Error al consultar el estado de sincronización.';
        setSyncError(msg);
      }
    }, 5000);
  }, [stopSyncPolling, MAX_SYNC_ATTEMPTS]);

  /**
   * Inicia la sincronización de la estación con el proveedor.
   * POST /api/v1/stations/{stationId}/sync → HTTP 202
   *
   * El trabajo se encola. El polling consulta el progreso.
   * No se permite duplicar mientras haya un sync activo.
   */
  const handleSync = useCallback(async () => {
    if (!stationId || syncLoading) return;
    if (syncStatus === 'PENDING' || syncStatus === 'RUNNING') return;

    setSyncLoading(true);
    setSyncError(null);
    setSyncMessage(null);

    try {
      const res = await stationsBackend.syncStation(stationId);
      setSyncStatus(res.status);
      setSyncMessage(res.message);

      if (res.status === 'PENDING' || res.status === 'RUNNING') {
        // Iniciar polling
        pollSyncStatus(stationId);
      } else if (res.status === 'COMPLETED') {
        setSyncLoading(false);
        setLastSyncAt(res.timestamp);
        SwalAlert.toast('Sincronización completada.', 'success');
      } else if (res.status === 'CONFLICT') {
        setSyncLoading(false);
        SwalAlert.toast('Se detectó un conflicto. Requiere revisión administrativa.', 'warning');
      } else if (res.status === 'FAILED') {
        setSyncLoading(false);
        setSyncError(res.message || 'La sincronización falló.');
      } else if (res.status === 'NOT_CONFIGURED') {
        setSyncLoading(false);
        setSyncMessage(res.message);
      }
    } catch (err: unknown) {
      setSyncLoading(false);
      setSyncStatus('FAILED');
      const msg = err instanceof Error ? err.message : 'Error al iniciar la sincronización.';
      setSyncError(msg);
      SwalAlert.toast(msg, 'error');
    }
  }, [stationId, syncLoading, syncStatus, pollSyncStatus]);

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      stopSyncPolling();
    };
  }, [stopSyncPolling]);

  // ── Helpers de display para sync ──

  const syncDisabled = syncLoading || syncStatus === 'PENDING' || syncStatus === 'RUNNING';

  const syncStatusBadge = !syncStatus ? null : (() => {
    switch (syncStatus) {
      case 'PENDING':
        return { label: 'Pendiente', icon: 'ri-time-line', color: 'bg-amber-100 text-amber-700 border-amber-200' };
      case 'RUNNING':
        return { label: 'Sincronizando', icon: 'ri-loader-4-line animate-spin', color: 'bg-primary-100 text-primary-700 border-primary-200' };
      case 'COMPLETED':
        return { label: 'Completada', icon: 'ri-checkbox-circle-fill', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
      case 'FAILED':
        return { label: 'Fallida', icon: 'ri-error-warning-fill', color: 'bg-red-100 text-red-700 border-red-200' };
      case 'CONFLICT':
        return { label: 'Conflicto', icon: 'ri-alert-fill', color: 'bg-amber-100 text-amber-700 border-amber-200' };
      case 'NOT_CONFIGURED':
        return { label: 'Sin configurar', icon: 'ri-settings-3-line', color: 'bg-background-200 text-foreground-500 border-background-300' };
      default:
        return null;
    }
  })();

  const statusConfig = !result ? null : result.status === 'online'
    ? { label: 'Online', icon: 'ri-checkbox-circle-fill', dotClass: 'bg-emerald-500', textClass: 'text-emerald-600', bgClass: 'bg-emerald-50 border-emerald-200', badgeClass: 'bg-emerald-100 text-emerald-700' }
    : result.status === 'offline'
      ? { label: 'Offline', icon: 'ri-close-circle-fill', dotClass: 'bg-amber-500', textClass: 'text-amber-600', bgClass: 'bg-amber-50 border-amber-200', badgeClass: 'bg-amber-100 text-amber-700' }
      : { label: 'Error', icon: 'ri-error-warning-fill', dotClass: 'bg-red-500', textClass: 'text-red-600', bgClass: 'bg-red-50 border-red-200', badgeClass: 'bg-red-100 text-red-700' };

  const isOnline = frecuencia.estado === 'online';
  const controlDisabled = controlLoading !== null;

  return (
    <div className="space-y-6">
      {/* ─── MANAGED STREAMING STATUS ─── */}
      {managedStreaming && (
        <ManagedStreamingSection
          data={managedStreaming}
          radioId={stationId}
          onRefresh={onManagedStreamingRefresh}
        />
      )}

      {/* ─── STREAM CONFIG ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-broadcast-line text-foreground-500 text-xs"></i>
          Configuración de Streaming
        </h4>
        <div className="space-y-4">
          {/* Estado + Controles */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-2xl bg-background-50 border border-background-200">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-3 h-3 rounded-full shrink-0 ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-red-400'}`}></div>
              <div className="min-w-0">
                <p className="text-foreground-800 text-xs font-semibold">
                  {isOnline ? 'Stream Activo' : 'Stream Detenido'}
                </p>
                <p className="text-foreground-400 text-[10px] truncate">
                  {isOnline ? `Transmitiendo en ${frecuencia.frecuenciaNum} — ${frecuencia.bitrate} ${frecuencia.formato}` : 'La transmisión está detenida.'}
                </p>
                {controlError && (
                  <p className="text-red-500 text-[10px] mt-0.5">{controlError}</p>
                )}
              </div>
            </div>

            {/* Botones de control */}
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
              {isOnline ? (
                <>
                  {/* STOP */}
                  <button
                    onClick={() => handleControlClick('STOP')}
                    disabled={controlDisabled}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${
                      controlDisabled
                        ? 'bg-background-200 text-foreground-300 cursor-not-allowed'
                        : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100'
                    }`}
                  >
                    {controlLoading === 'STOP' ? (
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                    ) : (
                      'Detener'
                    )}
                  </button>
                  {/* RESTART */}
                  <button
                    onClick={() => handleControlClick('RESTART')}
                    disabled={controlDisabled}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${
                      controlDisabled
                        ? 'bg-background-200 text-foreground-300 cursor-not-allowed'
                        : 'bg-amber-50 text-amber-600 border border-amber-200 hover:bg-amber-100'
                    }`}
                  >
                    {controlLoading === 'RESTART' ? (
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                    ) : (
                      'Reiniciar'
                    )}
                  </button>
                  {/* RELOAD */}
                  <button
                    onClick={() => handleControlClick('RELOAD')}
                    disabled={controlDisabled}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${
                      controlDisabled
                        ? 'bg-background-200 text-foreground-300 cursor-not-allowed'
                        : 'bg-secondary-50 text-secondary-600 border border-secondary-200 hover:bg-secondary-100'
                    }`}
                  >
                    {controlLoading === 'RELOAD' ? (
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                    ) : (
                      'Recargar'
                    )}
                  </button>
                </>
              ) : (
                /* START */
                <button
                  onClick={() => handleControlClick('START')}
                  disabled={controlDisabled}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap ${
                    controlDisabled
                      ? 'bg-background-200 text-foreground-300 cursor-not-allowed'
                      : 'bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  {controlLoading === 'START' ? (
                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                  ) : (
                    'Iniciar'
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Resultado de operación de control */}
          {controlResult && !controlError && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center gap-2.5">
              <i className="ri-checkbox-circle-fill text-emerald-500 text-sm"></i>
              <div>
                <p className="text-emerald-700 text-[11px] font-semibold">
                  {actionLabels[controlResult.operation as ControlAction]}: {controlResult.message}
                </p>
                <p className="text-emerald-500 text-[10px]">
                  Estado: {controlResult.previous_status} → {controlResult.status}
                </p>
              </div>
            </div>
          )}

          {/* ── Panel de Sincronización ── */}
          {stationId && (
            <div className="p-4 rounded-2xl bg-background-50 border border-background-200 space-y-3">
              {/* Header + Sync button */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                    <i className="ri-refresh-line text-foreground-500 text-xs"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-800 text-[11px] font-semibold">Sincronización con proveedor</p>
                    <p className="text-foreground-400 text-[10px] truncate">
                      {syncStatus === 'PENDING' && 'Trabajo encolado...'}
                      {syncStatus === 'RUNNING' && 'Sincronizando metadata...'}
                      {syncStatus === 'COMPLETED' && 'Metadata sincronizada.'}
                      {syncStatus === 'FAILED' && 'La última sincronización falló.'}
                      {syncStatus === 'CONFLICT' && 'Requiere revisión administrativa.'}
                      {syncStatus === 'NOT_CONFIGURED' && 'Proveedor no configurado.'}
                      {!syncStatus && 'Sincronizá la metadata con el servidor de streaming.'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleSync}
                  disabled={syncDisabled}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold transition-colors cursor-pointer whitespace-nowrap shrink-0 ${
                    syncDisabled
                      ? 'bg-background-200 text-foreground-300 cursor-not-allowed'
                      : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 active:scale-[0.98]'
                  }`}
                >
                  {syncLoading ? (
                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                  ) : (
                    <i className="ri-refresh-line text-xs"></i>
                  )}
                  {syncLoading ? 'Enviando...' : 'Sincronizar'}
                </button>
              </div>

              {/* Status badge */}
              {syncStatusBadge && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${syncStatusBadge.color}`}>
                    <i className={`${syncStatusBadge.icon} text-[10px]`}></i>
                    {syncStatusBadge.label}
                  </span>

                  {/* Last sync timestamp */}
                  {lastSyncAt && syncStatus === 'COMPLETED' && (
                    <span className="text-foreground-400 text-[10px]">
                      Última: {new Date(lastSyncAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}

              {/* Sync message */}
              {syncMessage && syncStatus !== 'COMPLETED' && syncStatus !== 'PENDING' && syncStatus !== 'RUNNING' && (
                <p className="text-foreground-600 text-[10px] leading-relaxed">{syncMessage}</p>
              )}

              {/* Sync error (sanitizado) */}
              {syncError && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                  <i className="ri-error-warning-line text-red-500 text-xs shrink-0 mt-px"></i>
                  <p className="text-red-600 text-[10px] leading-relaxed">{syncError}</p>
                </div>
              )}

              {/* Conflict panel */}
              {syncStatus === 'CONFLICT' && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
                  <div className="flex items-start gap-2">
                    <i className="ri-alert-fill text-amber-500 text-sm shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-amber-800 text-[11px] font-semibold">Conflicto detectado</p>
                      <p className="text-amber-600 text-[10px] leading-relaxed mt-0.5">
                        Hay diferencias entre la configuración local y la del proveedor. Esto requiere revisión administrativa. No se modificó ningún dato automáticamente.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <i className="ri-shield-check-line text-amber-500 text-[10px]"></i>
                    <span className="text-amber-700 text-[10px] font-medium">No se creó otra estación.</span>
                    <span className="text-amber-400 text-[10px]">·</span>
                    <i className="ri-link-unlink-m text-amber-500 text-[10px]"></i>
                    <span className="text-amber-700 text-[10px] font-medium">No se reasignó provider_account.</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Frecuencia de emisión <span className="text-red-500">*</span></label>
              <input type="text" value={frecuencia.frecuenciaNum} onChange={(e) => onChangeFrecuencia({ ...frecuencia, frecuenciaNum: e.target.value })} placeholder="Ej: 102.5 FM" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors" />
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Identificador RFW</label>
              <input type="text" value={frecuencia.frecuenciaWeb} onChange={(e) => onChangeFrecuencia({ ...frecuencia, frecuenciaWeb: e.target.value })} placeholder="radioactitud" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors" />
              <p className="text-foreground-300 text-[9px] mt-0.5">URL: radiofrecuenciaweb.com/r/{frecuencia.frecuenciaWeb || 'tucodigo'}</p>
            </div>
          </div>

          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Stream URL <span className="text-red-500">*</span></label>
            <input type="text" value={frecuencia.streamUrl} onChange={(e) => onChangeFrecuencia({ ...frecuencia, streamUrl: e.target.value })} placeholder="https://stream.turadio.com/live" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Puerto</label>
              <input type="number" value={frecuencia.streamPort} onChange={(e) => onChangeFrecuencia({ ...frecuencia, streamPort: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors" />
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Formato</label>
              <select value={frecuencia.formato} onChange={(e) => onChangeFrecuencia({ ...frecuencia, formato: e.target.value })} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none">
                {formatosStream.map((f) => (<option key={f} value={f}>{f}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Bitrate</label>
              <select value={frecuencia.bitrate} onChange={(e) => onChangeFrecuencia({ ...frecuencia, bitrate: e.target.value })} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none">
                {bitrates.map((b) => (<option key={b} value={b}>{b}</option>))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Password del Servidor</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={frecuencia.streamPassword} onChange={(e) => onChangeFrecuencia({ ...frecuencia, streamPassword: e.target.value })} className="w-full px-3 py-2 pr-9 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors" />
              <button onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
                <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-foreground-500 text-xs`}></i>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-foreground-600 text-[10px] font-semibold mb-1">URL de administración</label>
            <input type="text" value={frecuencia.icecastUrl} onChange={(e) => onChangeFrecuencia({ ...frecuencia, icecastUrl: e.target.value })} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors" />
          </div>

          <div className="p-4 rounded-2xl bg-background-50 border border-background-200 space-y-3">
            <div className="flex items-center justify-between">
              <div><p className="text-foreground-800 text-xs font-semibold">Auto Reconexión</p><p className="text-foreground-400 text-[10px]">Reintentar automáticamente si el stream se cae</p></div>
              <button onClick={() => onChangeFrecuencia({ ...frecuencia, autoReconexion: !frecuencia.autoReconexion })} className={`relative w-10 h-6 rounded-full transition-colors cursor-pointer ${frecuencia.autoReconexion ? 'bg-emerald-500' : 'bg-background-300'}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-background-50 rounded-full shadow-sm transition-transform ${frecuencia.autoReconexion ? 'translate-x-[18px]' : 'translate-x-0.5'}`}></span>
              </button>
            </div>
            {frecuencia.autoReconexion && (
              <div>
                <label className="block text-foreground-600 text-[10px] font-semibold mb-1">Número de reintentos</label>
                <input type="number" value={frecuencia.reintentos} onChange={(e) => onChangeFrecuencia({ ...frecuencia, reintentos: parseInt(e.target.value) || 1 })} min={1} max={20} className="w-24 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs font-mono outline-none focus:border-foreground-300 transition-colors" />
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-background-200">
            <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>

      <hr className="border-background-200" />

      {/* ─── STREAM VERIFIER ─── */}
      <div>
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <i className="ri-radar-line text-foreground-500 text-xs"></i>
          Verificar Stream
        </h4>
        <div className="space-y-4">
          <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text" value={testUrl}
                    onChange={(e) => { setTestUrl(e.target.value); setResult(null); setAlert(null); setPhase('idle'); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
                    placeholder="https://stream.turadio.com/live"
                    disabled={verifyInProgress}
                    className="w-full px-4 py-3 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 font-mono outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                  />
                </div>
                <div className="relative">
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-10 h-[46px] rounded-xl bg-background-100 border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
                    <i className="ri-arrow-down-s-line text-foreground-500 text-sm"></i>
                  </button>
                  {showDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)}></div>
                      <div className="absolute right-0 top-full mt-1 z-20 w-72 bg-background-50 border border-background-200 rounded-xl shadow-lg overflow-hidden">
                        <div className="px-3 py-2 border-b border-background-100"><p className="text-foreground-400 text-[9px] font-semibold uppercase">URLs de prueba</p></div>
                        {knownStreamUrls.map((item) => (
                          <button key={item.url} onClick={() => { setTestUrl(item.url); setShowDropdown(false); setResult(null); setAlert(null); setPhase('idle'); }} className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 cursor-pointer hover:bg-background-100 transition-colors ${testUrl === item.url ? 'bg-primary-50' : ''}`}>
                            <i className={`${testUrl === item.url ? 'ri-radio-button-fill text-primary-500' : 'ri-radio-button-line text-foreground-300'} text-xs`}></i>
                            <div className="min-w-0"><p className="text-xs font-medium truncate">{item.label}</p><p className="text-foreground-400 text-[10px] truncate font-mono">{item.url}</p></div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <button onClick={handleVerify} disabled={!testUrl.trim() || verifyInProgress}
              className={`mt-4 w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${verifyInProgress ? 'bg-foreground-200 text-foreground-400 cursor-wait' : !testUrl.trim() ? 'bg-foreground-100 text-foreground-300 cursor-not-allowed' : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 active:scale-[0.98]'}`}>
              {verifyInProgress ? (<><i className="ri-loader-4-line animate-spin text-base"></i><span>{phaseLabels[phase]}</span></>) : (<><i className="ri-play-fill text-base"></i><span>Probar transmisión</span></>)}
            </button>
          </div>

          {/* Result Section */}
          {result && alert && statusConfig && (
            <div className="space-y-4 animate-fade-in-up">
              <div className={`rounded-2xl border p-5 ${statusConfig.bgClass}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${result.status === 'online' ? 'bg-emerald-500' : result.status === 'offline' ? 'bg-amber-500' : 'bg-red-500'}`}>
                    <i className={`${statusConfig.icon} text-background-50 text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1"><h4 className="text-foreground-950 font-bold text-base">{alert.title}</h4><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${statusConfig.badgeClass}`}>{statusConfig.label}</span></div>
                    <p className="text-foreground-500 text-xs leading-relaxed">{alert.description}</p>
                    {result.status === 'online' && (
                      <div className="flex items-center gap-3 mt-4 flex-wrap">
                        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span><span className="text-emerald-600 text-[11px] font-semibold">{result.listeners} oyentes</span></div>
                        <span className="text-foreground-300 text-[10px]">·</span><span className="text-foreground-500 text-[11px]">Pico: {result.peakListeners}</span>
                        <span className="text-foreground-300 text-[10px]">·</span><span className="text-foreground-500 text-[11px]">{result.genre}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-background-50 border border-background-200 rounded-xl p-4"><p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Tiempo de respuesta</p><p className="text-foreground-950 font-bold text-lg font-mono">{result.responseTime > 0 ? `${result.responseTime} ms` : '-'}</p></div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-4"><p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Formato</p><p className="text-foreground-950 font-bold text-lg font-mono">{result.detectedFormat}</p><p className="text-foreground-400 text-[10px] mt-0.5">{result.detectedBitrate}</p></div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-4"><p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Codec</p><p className="text-foreground-950 font-bold text-sm font-mono">{result.audioCodec}</p><p className="text-foreground-400 text-[10px] mt-0.5">{result.channels} · {result.sampleRate}</p></div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-4"><p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Servidor</p><p className="text-foreground-950 font-bold text-sm font-mono">{result.serverType}</p></div>
              </div>
            </div>
          )}

          {/* Tips */}
          <div className="bg-secondary-50 border border-secondary-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shrink-0"><i className="ri-lightbulb-line text-secondary-500 text-sm"></i></div>
              <div>
                <h4 className="text-secondary-700 font-bold text-xs mb-1.5">Consejos para un stream confiable</h4>
                <ul className="space-y-1">
                  <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]"><i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>Si usás tu propio servidor, asegurate de que sea dedicado y no un hosting compartido.</li>
                  <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]"><i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>La calidad de audio recomendada es estándar (128 kbps) o alta (192 kbps).</li>
                  <li className="flex items-start gap-1.5 text-secondary-600/80 text-[10px]"><i className="ri-check-line text-secondary-500 text-[10px] mt-px shrink-0"></i>Mantené tu software de emisión siempre actualizado para evitar cortes inesperados.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}